import {
  BoxDashboardKpiDtoSchema,
  CommitedClubListDtoSchema,
  DailyScheduleListSchema,
  DashboardFinalRoundListDtoSchema,
  DashboardRookieMembersListDtoSchema,
  InactiveMembersListDtoSchema,
  TeamTaskDtoSchema,
  TeamTasksListDtoSchema,
  TotalFinalRoundDtoSchema,
  TotalRookiesDtoSchema,
  WeeklyTrialsDropInsBirthdaysDtoSchema,
  type BoxDashboardKpiDto,
  type CommitedClubDto,
  type DailyScheduleListDto,
  type DashboardFinalRoundDto,
  type DashboardRookieMemberDto,
  type FinalRoundType,
  type InactiveMemberDto,
  type RookieMilestone,
  type TeamTaskDto,
  type TotalFinalRoundDto,
  type TotalRookiesDto,
  type UpsertTeamTaskDto,
  type WeeklyTrialsDropInsBirthdaysDto,
} from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import type { BoxDashboardApiGateway } from "@/modules/boxDashboard/gateway/boxDashboardApiGateway"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import type { Paginated } from "@modules/utils/paginated"

export class BoxDashboardApi implements BoxDashboardApiGateway {
  private dashboardApiUrl = `/dashboard`

  constructor(private api: ApiClient) {}

  async getDailySchedule(): Promise<DailyScheduleListDto> {
    const resultDto = await this.api.get<DailyScheduleListDto>(`${this.dashboardApiUrl}/daily-schedule`)
    return LoggerService.parseApiDto(DailyScheduleListSchema, resultDto.data)
  }

  async getTrialsDropInsBirthdays(startDate: string, endDate: string): Promise<WeeklyTrialsDropInsBirthdaysDto> {
    const resultDto = await this.api.get<WeeklyTrialsDropInsBirthdaysDto>(
      `${this.dashboardApiUrl}/trials-dropins-birthdays`,
      {
        params: { startDate, endDate },
      },
    )
    return LoggerService.parseApiDto(WeeklyTrialsDropInsBirthdaysDtoSchema, resultDto.data)
  }

  async getActiveTasks(page = 0, pageSize = 20): Promise<Paginated<TeamTaskDto>> {
    const resultDto = await this.api.get<Paginated<TeamTaskDto>>(`${this.dashboardApiUrl}/active-tasks`, {
      params: { page, size: pageSize },
    })
    return { ...resultDto.data, content: LoggerService.parseApiDto(TeamTasksListDtoSchema, resultDto.data.content) }
  }

  async getCompletedTasks(page = 0, pageSize = 20): Promise<Paginated<TeamTaskDto>> {
    const resultDto = await this.api.get<Paginated<TeamTaskDto>>(`${this.dashboardApiUrl}/completed-tasks`, {
      params: { page, size: pageSize },
    })

    return { ...resultDto.data, content: LoggerService.parseApiDto(TeamTasksListDtoSchema, resultDto.data.content) }
  }

  async createTeamTask(data: UpsertTeamTaskDto): Promise<TeamTaskDto> {
    const resultDto = await this.api.post<TeamTaskDto>(`${this.dashboardApiUrl}/task`, data)
    return LoggerService.parseApiDto(TeamTaskDtoSchema, resultDto.data)
  }

  async updateTeamTask(id: string, data: UpsertTeamTaskDto): Promise<TeamTaskDto> {
    const resultDto = await this.api.put<TeamTaskDto>(`${this.dashboardApiUrl}/task/${id}`, data)
    return LoggerService.parseApiDto(TeamTaskDtoSchema, resultDto.data)
  }

  async deleteTeamTask(id: string): Promise<void> {
    await this.api.delete(`${this.dashboardApiUrl}/task/${id}`)
  }

  async completeTeamTask(id: string): Promise<TeamTaskDto> {
    const resultDto = await this.api.put<TeamTaskDto>(`${this.dashboardApiUrl}/task/${id}/complete`)
    return LoggerService.parseApiDto(TeamTaskDtoSchema, resultDto.data)
  }

  async getInactiveMembers(page = 0, pageSize = 10): Promise<Paginated<InactiveMemberDto>> {
    const resultDto = await this.api.get<Paginated<InactiveMemberDto>>(`${this.dashboardApiUrl}/member/inactive`, {
      params: { page, size: pageSize },
    })
    return {
      ...resultDto.data,
      content: LoggerService.parseApiDto(InactiveMembersListDtoSchema, resultDto.data.content),
    }
  }

  async archiveInactiveMember(id: string): Promise<void> {
    await this.api.delete(`${this.dashboardApiUrl}/member/inactive/${id}`)
  }

  async getTotalRookies(): Promise<TotalRookiesDto> {
    const resultDto = await this.api.get<TotalRookiesDto>(`${this.dashboardApiUrl}/member/numberOfRookies`)
    return LoggerService.parseApiDto(TotalRookiesDtoSchema, resultDto.data)
  }

  async getRookieMembers(
    milestone: RookieMilestone = "UNDER_15_DAYS",
    page = 0,
    pageSize = 10,
  ): Promise<Paginated<DashboardRookieMemberDto>> {
    const resultDto = await this.api.get<Paginated<DashboardRookieMemberDto>>(`${this.dashboardApiUrl}/member/rookie`, {
      params: { rookieMilestones: milestone, page, size: pageSize },
    })
    return {
      ...resultDto.data,
      content: LoggerService.parseApiDto(DashboardRookieMembersListDtoSchema, resultDto.data.content),
    }
  }

  async getTotalFinalRound(): Promise<TotalFinalRoundDto> {
    const resultDto = await this.api.get<TotalFinalRoundDto>(`${this.dashboardApiUrl}/member/numberOfFinalRound`)
    return LoggerService.parseApiDto(TotalFinalRoundDtoSchema, resultDto.data)
  }

  async getFinalRoundMembers(
    offerType?: FinalRoundType,
    page = 0,
    pageSize = 10,
  ): Promise<Paginated<DashboardFinalRoundDto>> {
    const resultDto = await this.api.get<Paginated<DashboardFinalRoundDto>>(
      `${this.dashboardApiUrl}/member/finalRound`,
      {
        params: { filter: offerType, page, size: pageSize },
      },
    )
    return {
      ...resultDto.data,
      content: LoggerService.parseApiDto(DashboardFinalRoundListDtoSchema, resultDto.data.content),
    }
  }

  async getCommitedClub(params: {
    periodType: "MONTH" | "YEAR"
    year: number
    month?: number
  }): Promise<Paginated<CommitedClubDto>> {
    const resultDto = await this.api.get<Paginated<CommitedClubDto>>(`${this.dashboardApiUrl}/member/commitedClub`, {
      params,
    })
    return { ...resultDto.data, content: LoggerService.parseApiDto(CommitedClubListDtoSchema, resultDto.data.content) }
  }

  async getKPI(period: {
    baseline: {
      year: number
      month?: number
      quarter?: number
    }
    comparison: {
      year: number
      month?: number
      quarter?: number
    }
  }): Promise<BoxDashboardKpiDto> {
    const resultDto = await this.api.post<BoxDashboardKpiDto>(`${this.dashboardApiUrl}/kpi`, {
      baseline: period.baseline,
      comparison: period.comparison,
    })
    return LoggerService.parseApiDto(BoxDashboardKpiDtoSchema, resultDto.data)
  }
}
